import { useCallback, useEffect, useState } from "react";

import { useAuthContext } from "@/components/AuthProvider";
import Box from "@/components/Box";
import CustomerPage from "@/components/CustomerPage";
import AuthFlowHeader from "@/components/otp/AuthFlowHeader";
import { theme } from "@/core/theme";
import { fetchOtpSetup, startOtpReplacement, verifyOtpSetup } from "@/hooks/useOtpApi";
import { PanelBoxV2 } from "@/tpo/Boxes";
import Center from "@/tpo/Center";
import ChevronComponent from "@/tpo/Chevron";
import Input from "@/tpo/Input";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";

const TOKEN_LENGTH = 6;

function CurrentDeviceForm({ token, error, submitting, onTokenChange, onSubmit, onCancel }) {
  return (
    <Stack gap={20}>
      <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="130%">
        Enter the 6-digit code from your current authenticator app to continue.
      </Box>

      <Stack gap={8}>
        <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="26px">
          Current verification code
        </Box>
        <Input
          value={token}
          onChange={event =>
            onTokenChange(event.target.value.replace(/\D/g, "").slice(0, TOKEN_LENGTH))
          }
          onKeyDown={event => {
            if (event.key === "Enter" && token.length === TOKEN_LENGTH) {
              onSubmit();
            }
          }}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={TOKEN_LENGTH}
          placeholder="000000"
          disabled={submitting}
          width="100%"
          px={2}
          py={2}
          height={50}
          borderRadius={5}
          border="1px solid"
          borderColor="carbon"
          bg="white"
          fontFamily="monospace"
          fontSize={18}
          letterSpacing={4}
          textAlign="center"
        />
        {error ? (
          <Box color="red" fontSize={14}>
            {error}
          </Box>
        ) : null}
        <Center gap={20} flexWrap="wrap" mt={8}>
          {onCancel ? (
            <ButtonV2
              type="button"
              color="red"
              rightIcon={<ChevronComponent />}
              size={["sm", "sm", "md"]}
              onClick={onCancel}
            >
              Cancel
            </ButtonV2>
          ) : null}
          <ButtonV2
            color="green"
            type="button"
            rightIcon={<ChevronComponent />}
            size={["sm", "sm", "md"]}
            disabled={token.length !== TOKEN_LENGTH || submitting}
            onClick={onSubmit}
          >
            {submitting ? "Verifying…" : "Continue"}
          </ButtonV2>
        </Center>
      </Stack>
    </Stack>
  );
}

function OtpSetupForm({
  loading,
  success,
  qrDataUri,
  qrLoadError,
  replacing,
  required,
  token,
  error,
  submitting,
  onTokenChange,
  onVerify,
  onCancel,
  onQrError
}) {
  if (loading) {
    return <Box fontFamily="gilroyMedium">Loading two-factor setup…</Box>;
  }

  if (success) {
    return (
      <Stack gap={16}>
        <Box fontFamily="gilroyBold" fontSize={[20, 20, 24]} color="green">
          {replacing ? "Authenticator updated" : "Two-factor authentication enabled"}
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="130%">
          {replacing
            ? "Your authenticator app has been updated. Use the new app for sign-in codes."
            : "Your authenticator app is now linked to your account."}
        </Box>
      </Stack>
    );
  }

  return (
    <Stack gap={20}>
      {required ? (
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="130%">
          Two-factor authentication is required for your account. Set up an authenticator app to
          continue.
        </Box>
      ) : null}

      <Stack gap={12}>
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="130%">
          1. Install an authenticator app (Google Authenticator, Authy, 1Password, etc.)
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="130%">
          2. Scan the QR code below with your app
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="130%">
          3. Enter the 6-digit code to verify
        </Box>
      </Stack>

      <Stack gap={16}>
        <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="26px">
          QR code
        </Box>
        {error && !qrDataUri ? (
          <Box color="red" fontSize={14}>
            {error}
          </Box>
        ) : null}
        {qrDataUri ? (
          <Box maxWidth={280} width="100%">
            <Box
              as="img"
              src={qrDataUri}
              alt="TOTP QR Code"
              width="100%"
              display="block"
              bg="white"
              p={8}
              borderRadius={5}
              border="1px solid"
              borderColor="carbon"
              onError={() => onQrError?.()}
            />
          </Box>
        ) : null}
        {qrLoadError ? (
          <Box color="red" fontSize={14}>
            Could not display the QR code. Please refresh and try again.
          </Box>
        ) : null}
      </Stack>

      <Stack gap={8}>
        <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="26px">
          Verification code
        </Box>
        <Input
          value={token}
          onChange={event =>
            onTokenChange(event.target.value.replace(/\D/g, "").slice(0, TOKEN_LENGTH))
          }
          onKeyDown={event => {
            if (event.key === "Enter" && token.length === TOKEN_LENGTH) {
              onVerify();
            }
          }}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={TOKEN_LENGTH}
          placeholder="000000"
          disabled={submitting}
          width="100%"
          px={2}
          py={2}
          height={50}
          borderRadius={5}
          border="1px solid"
          borderColor="carbon"
          bg="white"
          fontFamily="monospace"
          fontSize={18}
          letterSpacing={4}
          textAlign="center"
        />
        {error && qrDataUri ? (
          <Box color="red" fontSize={14}>
            {error}
          </Box>
        ) : null}
        <Center gap={20} flexWrap="wrap" mt={8}>
          {!required && onCancel ? (
            <ButtonV2
              type="button"
              color="red"
              rightIcon={<ChevronComponent />}
              size={["sm", "sm", "md"]}
              onClick={onCancel}
            >
              Cancel
            </ButtonV2>
          ) : null}
          <ButtonV2
            color="green"
            type="button"
            rightIcon={<ChevronComponent />}
            size={["sm", "sm", "md"]}
            disabled={token.length !== TOKEN_LENGTH || submitting}
            onClick={onVerify}
          >
            {submitting ? "Verifying…" : "Verify"}
          </ButtonV2>
        </Center>
      </Stack>
    </Stack>
  );
}

export default function OtpSetup({
  embedded = false,
  fullPage = false,
  required = false,
  replacing = false,
  initialQrDataUri,
  onComplete,
  onCancel
}) {
  const { csrfToken, setState } = useAuthContext();
  const [needsCurrentDeviceCode, setNeedsCurrentDeviceCode] = useState(
    replacing && !initialQrDataUri
  );
  const [currentToken, setCurrentToken] = useState("");
  const [loading, setLoading] = useState(!initialQrDataUri && !needsCurrentDeviceCode);
  const [qrDataUri, setQrDataUri] = useState(initialQrDataUri || "");
  const [qrLoadError, setQrLoadError] = useState(false);
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (initialQrDataUri || needsCurrentDeviceCode || replacing || qrDataUri) {
      return undefined;
    }

    let cancelled = false;

    fetchOtpSetup(csrfToken)
      .then(data => {
        if (cancelled) return;
        if (!data.qrDataUri) {
          throw new Error("Could not load QR code.");
        }
        setQrDataUri(data.qrDataUri);
        setQrLoadError(false);
      })
      .catch(setupError => {
        if (cancelled) return;
        setError(setupError.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [csrfToken, initialQrDataUri, needsCurrentDeviceCode, replacing, qrDataUri]);

  const handleCurrentDeviceSubmit = useCallback(async () => {
    if (currentToken.length !== TOKEN_LENGTH) {
      setError("Enter the 6-digit code from your current authenticator app.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const data = await startOtpReplacement(csrfToken, currentToken);
      if (!data.qrDataUri) {
        throw new Error("Could not load QR code.");
      }
      setQrDataUri(data.qrDataUri);
      setNeedsCurrentDeviceCode(false);
      setCurrentToken("");
      setError("");
      setLoading(false);
    } catch (replacementError) {
      setError(replacementError.message);
      setCurrentToken("");
    } finally {
      setSubmitting(false);
    }
  }, [csrfToken, currentToken]);

  const handleVerify = useCallback(async () => {
    if (token.length !== TOKEN_LENGTH) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await verifyOtpSetup(csrfToken, token);
      setSuccess(true);
      setState(prev => ({
        ...prev,
        otp: {
          ...prev.otp,
          hasDevice: true,
          verified: true,
          setupRequired: false
        }
      }));
      onComplete?.();
    } catch (verifyError) {
      setError(verifyError.message);
      setToken("");
    } finally {
      setSubmitting(false);
    }
  }, [csrfToken, onComplete, setState, token]);

  const form = needsCurrentDeviceCode ? (
    <CurrentDeviceForm
      token={currentToken}
      error={error}
      submitting={submitting}
      onTokenChange={nextToken => {
        setCurrentToken(nextToken);
        setError("");
      }}
      onSubmit={handleCurrentDeviceSubmit}
      onCancel={onCancel}
    />
  ) : (
    <OtpSetupForm
      loading={loading}
      success={success}
      qrDataUri={qrDataUri}
      qrLoadError={qrLoadError}
      replacing={replacing}
      required={required}
      token={token}
      error={error}
      submitting={submitting}
      onTokenChange={nextToken => {
        setToken(nextToken);
        setError("");
      }}
      onVerify={handleVerify}
      onCancel={onCancel}
      onQrError={() => setQrLoadError(true)}
    />
  );

  if (embedded) {
    return form;
  }

  const panel = (
    <PanelBoxV2
      maxWidth={600}
      stacked
      gap={40}
      outer={{
        pt: theme.spacing.section.pt,
        pb: theme.spacing.section.pb,
        px: 20
      }}
    >
      <Box>
        <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]} lineHeight="130%">
          Two-factor authentication
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="130%" mt={20}>
          Protect your account with an authenticator app.
        </Box>
      </Box>
      {form}
    </PanelBoxV2>
  );

  if (fullPage) {
    return (
      <>
        <AuthFlowHeader />
        <CustomerPage
          jumbotronProps={{
            title: "Two-factor authentication",
            bg: "dark",
            color: "white"
          }}
        >
          {panel}
        </CustomerPage>
      </>
    );
  }

  return panel;
}
